import React from 'react'
import { useState } from 'react'
// Bootstrap Imports
import ToggleButton from 'react-bootstrap/ToggleButton'
import ToggleButtonGroup from 'react-bootstrap/ToggleButtonGroup'
import ButtonToolbar from 'react-bootstrap/ButtonToolbar'
import Button from 'react-bootstrap/Button'
import Navbar from 'react-bootstrap/Navbar'

interface QuoteOptionHeaderProps {
   formType: number
   opportunity_id: string
   account_name: string
   opportunity_name: string
   opportunity_contact_name: string
   opportunity_contact_email: string
   last_agent_email: string
   last_agent_name: string
   invoice_number: string
   onTypeChangeCallback: (value: number) => void
   onGeneratePdfCallback: (
      opportunity_id: string,
      formType: number,
      account_name: string,
      opportunity_name: string,
      opportunity_contact_name: string,
      opportunity_contact_email: string,
      last_agent_email: string,
      last_agent_name: string,
      invoice_number: string,
      monthlyGrandTotal: number,
      activationGrandTotal: number,
      calculatedValues: any,
      user_role: string,
   ) => Promise<void>
   onRefreshCallback: () => void
   monthlyGrandTotal: number
   activationGrandTotal: number
   calculatedValues: any
   user_role: string
   allowInvoiceQuoteOption?: boolean
   isBooked?: boolean
}

/**
 * Sanitizes URL parameters by converting null, undefined, or 'null' string values to empty strings
 * @param {*} value - The value to sanitize
 * @returns {string} - The sanitized value
 */
function sanitizeUrlParam(value: any) {
   if (value === null || value === undefined || value === 'null') {
      return ''
   }
   return value
}

export default function QuoteOptionHeader({
   formType,
   opportunity_id,
   account_name,
   opportunity_name,
   opportunity_contact_name,
   opportunity_contact_email,
   last_agent_email,
   last_agent_name,
   invoice_number,
   onTypeChangeCallback,
   onGeneratePdfCallback,
   onRefreshCallback,
   monthlyGrandTotal,
   activationGrandTotal,
   calculatedValues,
   user_role,
   allowInvoiceQuoteOption = false,
   isBooked = false,
}: QuoteOptionHeaderProps) {
   const [isGenerating, setIsGenerating] = useState(false)
   return (
      <Navbar
         sticky='top'
         style={{ paddingTop: '1em' }}
         variant='light'
         bg='transparent'
      >
         <ButtonToolbar
            aria-label='Quote Control Toolbar'
            role='toolbar'
            className='justify-content-center mx-auto'
         >
            <ToggleButtonGroup
               className='mx-2'
               type='radio'
               name='options'
               value={formType}
               role='group'
            >
               <ToggleButton
                  type='radio'
                  id='tbg-radio-1'
                  value={1}
                  disabled={isGenerating || isBooked}
                  onChange={(e) => onTypeChangeCallback(Number(e.target.value))}
               >
                  Quote
               </ToggleButton>
               <ToggleButton
                  type='radio'
                  id='tbg-radio-2'
                  value={2}
                  disabled={isGenerating || isBooked}
                  onChange={(e) => onTypeChangeCallback(Number(e.target.value))}
               >
                  Prepayment Request
               </ToggleButton>
               {allowInvoiceQuoteOption && (
                  <ToggleButton
                     type='radio'
                     id='tbg-radio-3'
                     value={3}
                     disabled={isGenerating || isBooked}
                     onChange={(e) =>
                        onTypeChangeCallback(Number(e.target.value))
                     }
                  >
                     Proforma Invoice
                  </ToggleButton>
               )}
               {isBooked && (
                  <ToggleButton
                     type='radio'
                     id='tbg-radio-4'
                     value={4}
                     disabled={true}
                     checked={formType === 4}
                  >
                     Order Form
                  </ToggleButton>
               )}
            </ToggleButtonGroup>
            <Button
               className='me-2'
               as='input'
               type='reset'
               value='Refresh'
               disabled={isGenerating}
               onClick={(e) => {
                  setIsGenerating(true)
                  onRefreshCallback()
               }}
            />
            <Button
               className='me-2'
               as='input'
               type='submit'
               value={
                  opportunity_id.substring(0, 4) == 'ACC-'
                     ? 'Save to Account'
                     : 'Save to Opp'
               }
               disabled={isGenerating}
               onClick={async (e) => {
                  setIsGenerating(true)
                  // Sanitize all URL parameters to handle null values
                  const sanitizedFormType = sanitizeUrlParam(formType)
                  const sanitizedOpportunityId =
                     sanitizeUrlParam(opportunity_id)
                  const sanitizedOpportunityContactName = sanitizeUrlParam(
                     opportunity_contact_name,
                  )
                  const sanitizedOpportunityContactEmail = sanitizeUrlParam(
                     opportunity_contact_email,
                  )
                  const sanitizedLastAgentEmail =
                     sanitizeUrlParam(last_agent_email)
                  const sanitizedLastAgentName =
                     sanitizeUrlParam(last_agent_name)
                  const sanitizedInvoiceNumber =
                     sanitizeUrlParam(invoice_number)
                  const sanitizedMonthlyCost =
                     sanitizeUrlParam(monthlyGrandTotal)
                  const sanitizedDueAtActivation =
                     sanitizeUrlParam(activationGrandTotal)
                  const sanitizedUserRole = sanitizeUrlParam(user_role)
                  await onGeneratePdfCallback(
                     sanitizedOpportunityId,
                     sanitizedFormType,
                     account_name,
                     opportunity_name,
                     sanitizedOpportunityContactName,
                     sanitizedOpportunityContactEmail,
                     sanitizedLastAgentEmail,
                     sanitizedLastAgentName,
                     sanitizedInvoiceNumber,
                     sanitizedMonthlyCost,
                     sanitizedDueAtActivation,
                     calculatedValues,
                     sanitizedUserRole,
                  )
                  setIsGenerating(false)
               }}
            />
         </ButtonToolbar>
      </Navbar>
   )
}
